import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ContentCard = ({ content }) => {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const shareUrl = `${window.location?.origin}/knowledge/${content?.id}`;
  const shareTitle = content?.title;
  const shareDescription = content?.description;

  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    const encodedDescription = encodeURIComponent(shareDescription);
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`
    };

    if (shareUrls?.[platform]) {
      window.open(shareUrls?.[platform], '_blank', 'width=600,height=400');
    }
    
    setShowShareMenu(false);
  };

  const difficultyColors = {
    beginner: 'bg-success/10 text-success',
    intermediate: 'bg-warning/10 text-warning',
    advanced: 'bg-error/10 text-error'
  };

  const difficultyLabels = {
    beginner: 'Beginner',
    intermediate: 'Gemiddeld',
    advanced: 'Gevorderd'
  };

  return (
    <motion.div
      whileHover={{ 
        y: -12, 
        scale: 1.03,
        rotateX: 4,
        boxShadow: "0 20px 40px -10px rgba(0, 0, 0, 0.2), 0 0 15px rgba(16, 185, 129, 0.08)",
        transition: { duration: 0.3 } 
      }}
      transition={{ duration: 0.3 }}
      className="bg-card border border-border rounded-xl overflow-hidden shadow-[0_8px_24px_-4px_rgba(0,0,0,0.12)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.2)] transition-all h-full flex flex-col transform perspective-1000"
      style={{ transformStyle: 'preserve-3d' }}
    >
      {/* Image */}
      <div className="relative aspect-[16/9] overflow-hidden">
        <Image
          src={content?.image}
          alt={content?.title}
          className="w-full h-full object-cover"
        />
        
        {/* Verified Badge */}
        {content?.verified && (
          <div className="absolute top-3 left-3 bg-success text-success-foreground px-2 py-1 rounded-full flex items-center space-x-1 text-xs font-body font-medium">
            <Icon name="ShieldCheck" size={14} />
            <span>Geverifieerd</span>
          </div>
        )}
        
        {/* Bookmark Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsBookmarked(!isBookmarked)}
          className="absolute top-3 right-3 w-9 h-9 bg-card/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-card transition-colors"
        >
          <Icon 
            name={isBookmarked ? "Bookmark" : "Bookmark"} 
            size={18} 
            className={isBookmarked ? "text-primary fill-current" : "text-muted-foreground"}
          />
        </motion.button>
      </div>
      {/* Content */}
      <div className="p-5 flex-1 flex flex-col">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {content?.tags?.slice(0, 2)?.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-primary/10 text-primary text-xs font-body rounded-md"
            >
              {tag}
            </span>
          ))}
        </div>
        
        {/* Title */}
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2 line-clamp-2 hover:text-primary transition-colors cursor-pointer">
          {content?.title}
        </h3>
        
        {/* Description */}
        <p className="text-sm font-body text-muted-foreground mb-4 line-clamp-3 flex-1">
          {content?.description}
        </p>
        
        {/* Metadata */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
          <div className="flex items-center space-x-3 text-xs font-body text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} />
              <span>{content?.readTime}</span>
            </div>
            <div className={`px-2 py-1 rounded-md ${difficultyColors?.[content?.difficulty]}`}>
              {difficultyLabels?.[content?.difficulty]}
            </div>
          </div>
          
          <div className="text-xs font-body text-muted-foreground">
            {new Date(content?.publishDate)?.toLocaleDateString('nl-NL', { 
              day: 'numeric', 
              month: 'short', 
              year: 'numeric' 
            })}
          </div>
        </div>
        
        {/* Author */}
        <div className="mb-4">
          <p className="text-sm font-body font-medium text-foreground">{content?.author}</p>
          <p className="text-xs font-body text-muted-foreground">{content?.authorCredentials}</p>
        </div>
        
        {/* Social Share Buttons */}
        <div className="relative">
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="BookOpen"
              iconPosition="left"
              className="flex-1"
            >
              Lees artikel
            </Button>
            
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="w-9 h-9 border border-border rounded-md flex items-center justify-center hover:bg-muted transition-colors"
              >
                <Icon name="Share2" size={16} className="text-foreground" />
              </motion.button>
              
              {/* Share Menu */}
              {showShareMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute bottom-full right-0 mb-2 bg-card border border-border rounded-lg shadow-elevation-2 p-2 min-w-[160px] z-50"
                >
                  <p className="text-xs font-body font-medium text-muted-foreground px-2 py-1 mb-1">
                    Delen via:
                  </p>
                  
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-full flex items-center space-x-2 px-3 py-2 hover:bg-muted rounded-md transition-colors text-left"
                  >
                    <Icon name="Facebook" size={16} className="text-[#1877F2]" />
                    <span className="text-sm font-body text-foreground">Facebook</span>
                  </button>
                  
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-full flex items-center space-x-2 px-3 py-2 hover:bg-muted rounded-md transition-colors text-left"
                  >
                    <Icon name="Twitter" size={16} className="text-[#1DA1F2]" />
                    <span className="text-sm font-body text-foreground">Twitter</span>
                  </button>
                  
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-full flex items-center space-x-2 px-3 py-2 hover:bg-muted rounded-md transition-colors text-left"
                  >
                    <Icon name="Linkedin" size={16} className="text-[#0A66C2]" />
                    <span className="text-sm font-body text-foreground">LinkedIn</span>
                  </button>
                  
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="w-full flex items-center space-x-2 px-3 py-2 hover:bg-muted rounded-md transition-colors text-left"
                  >
                    <Icon name="MessageCircle" size={16} className="text-[#25D366]" />
                    <span className="text-sm font-body text-foreground">WhatsApp</span>
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ContentCard;